// Kleine, sichere GA4-Integration ohne Drittlibs, Consent-fähig

import { readConsent } from "./consent";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID ?? "";

const isProd = process.env.NODE_ENV === "production";

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

let scriptLoaded = false;
let configDone = false;

/** ---- NEU: offizieller "Hard-Off" Schalter ---- */
const GA_DISABLE_KEY = (`ga-disable-${GA_ID}`) as const;

function setGaDisabled(v: boolean) {
  if (typeof window === "undefined" || !GA_ID) return;
  (window as any)[GA_DISABLE_KEY] = v;
}

/** Optional: GA-Cookies entfernen nach Widerruf */
function clearGaCookies() {
  try {
    document.cookie
      .split(";")
      .map((c) => c.split("=")[0].trim())
      .forEach((name) => {
        if (name === "_ga" || name === "_gid" || name.startsWith("_ga_")) {
          document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
        }
      });
  } catch {}
}

function loadGaScript() {
  if (!isProd || !GA_ID || scriptLoaded) return;

  // ---- NEU: standardmäßig blockieren, bevor das Script lädt
  setGaDisabled(true);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer!.push(arguments);
  };

  window.gtag!("js", new Date());

  // Standard: ALLES DENIED, bis der Nutzer zustimmt
  window.gtag!("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    personalization_storage: "denied",
    security_storage: "granted",
  });

  scriptLoaded = true;
}

/** Nur intern: GA4 konfigurieren (erst, wenn Analytics erlaubt ist) */
function ensureConfig() {
  if (!isProd || !GA_ID || !window.gtag || configDone) return;
  // send_page_view: false → wir senden Pageviews manuell
  window.gtag!("config", GA_ID, {
    send_page_view: false,
    anonymize_ip: true,
    allow_google_signals: false,
  });
  configDone = true;
}

/** (Alt) blieb für Rückwärtskompatibilität: lädt nur Script + denied-Consent */
export function initAnalytics() {
  loadGaScript();
}

/** Consent anwenden: true = aktivieren, false = deaktivieren */
export function applyAnalyticsConsent(allowed: boolean) {
  if (!isProd || !GA_ID) return;

  if (allowed) {
    setGaDisabled(false);       // ---- NEU: erlauben
    loadGaScript();
    ensureConfig();
    window.gtag?.("consent", "update", { analytics_storage: "granted" });
  } else {
    setGaDisabled(true);        // ---- NEU: blockieren (Hard-Off)
    if (scriptLoaded) {
      window.gtag?.("consent", "update", { analytics_storage: "denied" });
      clearGaCookies();         // optional, löscht _ga/_gid/_ga_*
    }
    // Script wird nicht geladen, wenn noch nicht vorhanden → Privacy by default
  }
}

/** Beim App-Start: vorhandene Auswahl berücksichtigen */
export function initAnalyticsFromStoredConsent() {
  const c = readConsent();
  if (c?.analytics) {
    applyAnalyticsConsent(true);
  } else {
    // Optional: Script vorladen, aber mit denied – lassen wir bewusst weg
  }
}

/** Pageview (sendet nur, wenn GA geladen + erlaubt ist) */
export function trackPageview(path: string) {
  if (!isProd || !GA_ID || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_location: window.location.origin + path,
    page_path: path,
    page_title: document.title,
  });
}

/** GA4 Events – niemals PII senden! */
export function trackEvent(
  name: string,
  params: Record<string, string | number | boolean | null | undefined> = {}
) {
  if (!isProd || !GA_ID || !window.gtag) return;
  const sanitized: Record<string, any> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    if (/email|name|message|text|note|comment|chat/i.test(k)) continue;
    sanitized[k] = v;
  }
  window.gtag("event", name, sanitized);
}
